<script setup lang="ts">
import { getInitials } from '@/utils/generalFunctions'
import { SearchSmIcon } from '@gohighlevel/ghl-icons/24/outline'
import { UIDropdown, UITooltip } from '@gohighlevel/ghl-ui'
import { computed, h, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  members: {
    type: Array as () => any[],
    required: true,
  },
  displayedMembers: {
    type: Array as () => any[],
    required: true,
  },
  maxVisible: {
    type: Number,
    default: 4,
  },
  modelValue: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['update:modelValue', 'edit-hours', 'select-user'])

const showDropdown = ref(false)
const searchQuery = ref('')

const visibleMembers = computed(() => props.members.slice(0, props.maxVisible))
const hiddenMembers = computed(() => props.members.slice(props.maxVisible))
const hasMoreMembers = computed(() => hiddenMembers.value.length > 0)

const filteredMembers = computed(() => {
  const members = [...props.displayedMembers, ...props.members]
  if (!searchQuery.value) {
    return members
  }
  return members.filter(member =>
    member.name.toLowerCase().includes(searchQuery.value.toLowerCase())
  )
})

const teamMembersOptions = computed(() => {
  return(
    [
      {
        label: t('settings.availability.schedule.search'),
        value: 'search',
        key: 'search',
      },
    ].concat(
      filteredMembers.value.map(member => ({
        key: member.userId,
        label: member.name,
        value: member.userId,
        profilePhoto: member.profilePhoto,
      }))
    )
  )
})

const isSelected = (memberId: string) => {
  return props.modelValue === memberId
}

const toggleSelection = (memberId: string) => {
  console.log('toggleSelection', memberId)
  emit('update:modelValue', memberId)
  emit(
    'select-user',
    [...props.displayedMembers, ...props.members].find(m => m.userId === memberId)
  )
}

const handleDropdownUserClick = (member: any) => {
  emit('update:modelValue', member.value)
  emit(
    'select-user',
    [...props.displayedMembers, ...props.members].find(m => m.userId === member.value)
  )
  showDropdown.value = false
  searchQuery.value = ''
}
const searchInputRef = ref<HTMLInputElement | null>(null)

const SearchIconNode = h(
  'div',
  {
    class:
      'absolute inset-y-0 left-0 flex items-center gap-2 pl-4 text-gray-300 pointer-events-none',
  },
  [
    h(SearchSmIcon, {
      class: 'text-gray-300',
      height: '20',
      style: {
        width: '20px',
        height: '20px',
      },
    }),
  ]
)
const renderOption = (prop: any) => {
  const option = prop.option

  const SearchElement = h(
    'div',
    { class: 'sticky top-0 w-full p-1 z-10 bg-white pt-[5px]' },
    [
      SearchIconNode,
      h('input', {
        ref: searchInputRef,
        key: 'user-search-input',
        class: `w-full rounded-md border-2 border-gray-300 p-2 text-gray-700 text-sm font-normal !pl-10`,
        placeholder: 'Search',
        type: 'text',
        value: searchQuery.value,
        onInput: e => {
          searchQuery.value = e.target.value
        },
        autocomplete: 'off',
      }),
    ]
  )

  if (option.key === 'search') {
    return SearchElement
  }

  // User option element
  const userOptionElement = h('div', {}, [
    h(
      'div',
      {
        class: 'px-2 py-1 w-full cursor-pointer hover:bg-gray-100',
        onClick: () => handleDropdownUserClick(option),
      },
      [
        h('div', { class: 'flex items-center gap-2' }, [
          h(
            'div',
            {
              class:
                'flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100',
            },
            // Conditionally render <img> or fallback initials
            option.profilePhoto
              ? h('img', {
                  src: option.profilePhoto,
                  alt: option.label,
                  class: 'h-10 w-10 rounded-full object-cover',
                })
              : h(
                  'div',
                  {
                    class:
                      'w-full rounded-full flex items-center justify-center',
                  },
                  h(
                    'span',
                    { class: 'text-sm font-normal text-gray-700' },
                    getInitials(option.label)
                  )
                )
          ),
          h(
            'div',
            {
              class:
                'w-full max-w-full word-break whitespace-normal truncate text-gray-600 truncate-2-lines',
            },
            {
              default: () => option.label,
            }
          ),
        ]),
      ]
    ),
  ])

  // Otherwise, just return the user option
  return userOptionElement
}
</script>

<template>
  <div class="flex items-center">
    <div class="flex items-center -space-x-2">
      <button
        v-for="member in visibleMembers"
        :key="member.userId"
        class="cursor-pointer rounded-full"
        :class="{
          'ring-2 ring-primary-500 ring-offset-2': isSelected(member.userId),
        }"
        @click="toggleSelection(member.userId)"
      >
        <UITooltip placement="top">
          <template #trigger>
            <div
              class="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100"
            >
              <img
                v-if="member.profilePhoto"
                :src="member.profilePhoto"
                :alt="member.name"
                class="h-10 w-10 rounded-full object-cover"
              />
              <div v-else class="w-full rounded-full">
                <span class="text-sm font-normal text-gray-700">
                  {{ getInitials(member.name) }}
                </span>
              </div>
            </div>
          </template>
          <p class="max-w-sm">
            {{ member.name }}
          </p>
        </UITooltip>
      </button>
    </div>
    <UIDropdown
      v-if="hasMoreMembers"
      id="team-member-avatar-group-dropdown"
      v-model:show="showDropdown"
      :options="teamMembersOptions"
      :value="modelValue"
      :show-arrow="true"
      placement="bottom-start"
      trigger="click"
      class="ml-2"
      :style="{ padding: 0 }"
      :render-option="renderOption"
      @clickoutside="showDropdown = false"
      @update:show="showDropdown = true"
    >
      <button
        class="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border-2 border-dashed border-gray-300 bg-gray-100 text-sm font-semibold text-gray-700"
        @click="showDropdown = true"
      >
        +{{ hiddenMembers.length }}
      </button>
    </UIDropdown>
  </div>
</template>

<style>
#team-member-avatar-group-dropdown {
  max-height: 300px !important;
  width: 220px !important;
  min-width: 220px !important;
  overflow-y: auto !important;
  overflow-x: hidden !important;
  padding-top: 0px !important;
  overscroll-behavior: contain;
  padding-bottom: 0px !important;
  border-radius: 8px !important;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
#team-member-avatar-group-dropdown::-webkit-scrollbar {
  display: none;
}
</style>
